import React, { useState, useMemo, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Search, Pin, PinOff, Pencil, Trash2, Clock, StickyNote,
  Package, User, Building, Handshake, ListTodo, CheckCircle2,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import NoteFormDialog from "@/components/NoteFormDialog";
import TaskFormDialog from "@/components/TaskFormDialog";
import { notifTimeAgo, taskStatusChip } from "@/lib/notif";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "pinned", label: "Pinned" },
  { key: "reminder", label: "With Reminder" },
  { key: "linked", label: "Linked" },
  { key: "unlinked", label: "Unlinked" },
];
const SORTS = [
  { key: "newest", label: "Newest first" },
  { key: "oldest", label: "Oldest first" },
  { key: "updated", label: "Recently updated" },
  { key: "reminder", label: "Reminder date" },
];

const LINK_META = {
  Asset: { Icon: Package, path: (id) => `/assets/${id}`, tint: "text-emerald-600 bg-emerald-50 border-emerald-100" },
  Lead: { Icon: User, path: (id) => `/leads/${id}`, tint: "text-indigo-600 bg-indigo-50 border-indigo-100" },
  Client: { Icon: Building, path: (id) => `/clients/${id}`, tint: "text-sky-600 bg-sky-50 border-sky-100" },
  Broker: { Icon: Handshake, path: (id) => `/brokers/${id}`, tint: "text-amber-600 bg-amber-50 border-amber-100" },
};

function fmtReminder(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}
function firstLine(s) { return (s || "").split("\n")[0]; }

// Note → Task creation prefill (additive; the note itself is never changed).
// Tasks support Asset/Lead/Broker only, so a Client link maps to Lead (same leads collection).
export function noteToTaskPrefill(n) {
  const title = (n.title || "").trim() || (n.content || "").slice(0, 60);
  let type = n.linked_record_type;
  if (type === "Client") type = "Lead";
  return {
    title,
    description: n.content || "",
    due_date: n.reminder_datetime ? n.reminder_datetime.slice(0, 10) : null,
    linked_record_type: type || null,
    linked_record_id: type ? n.linked_record_id : null,
    linked_record_name: type ? n.linked_record_name : null,
    source_note_id: n.id || null,
  };
}

function LinkedChip({ note, onNavigate }) {
  const meta = LINK_META[note.linked_record_type];
  if (!meta) return null;
  const { Icon, tint } = meta;
  if (note.linked_record_deleted) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-400" data-testid="note-chip-deleted">
        <Icon className="h-3 w-3" /> {note.linked_record_name || note.linked_record_type} <span className="italic">(deleted)</span>
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onNavigate(meta.path(note.linked_record_id)); }}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] hover:opacity-80 ${tint}`}
      data-testid="note-chip-link"
    >
      <Icon className="h-3 w-3" /> {note.linked_record_name || note.linked_record_type}
    </button>
  );
}

function NoteCard({ note, onEdit, onDelete, onPin, onConvert, highlighted, cardRef }) {
  const navigate = useNavigate();
  const title = note.title || firstLine(note.content) || "Untitled";
  return (
    <div
      ref={cardRef}
      onClick={() => onEdit(note)}
      className={`group cursor-pointer rounded-lg border bg-white p-4 transition-shadow hover:shadow-md ${highlighted ? "border-rose-300 ring-2 ring-rose-200" : "border-slate-200"}`}
      data-testid={`note-card-${note.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            {note.is_pinned && <Pin className="h-3.5 w-3.5 shrink-0 text-rose-500 fill-rose-500" data-testid="note-pinned-indicator" />}
            <h3 className="truncate font-semibold text-slate-900" data-testid="note-card-title">{title}</h3>
          </div>
          {note.content && (
            <p className="mt-1 line-clamp-2 whitespace-pre-wrap text-sm text-slate-600">{note.content}</p>
          )}
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            {note.linked_record_type && <LinkedChip note={note} onNavigate={navigate} />}
            {note.reminder_datetime && (
              <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-500" data-testid="note-reminder-chip">
                <Clock className="h-3 w-3" /> {fmtReminder(note.reminder_datetime)}
              </span>
            )}
            {note.converted_task_id && (
              note.converted_task_deleted ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-400" data-testid="note-task-chip-deleted">
                  <CheckCircle2 className="h-3 w-3" /> Task <span className="italic">(deleted)</span>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); navigate(`/tasks/${note.converted_task_id}`); }}
                  title={note.converted_task_title || ""}
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] hover:opacity-80 ${taskStatusChip(note.converted_task_status).cls}`}
                  data-testid="note-task-chip"
                >
                  <CheckCircle2 className="h-3 w-3 shrink-0" /> Task · {taskStatusChip(note.converted_task_status).label}
                </button>
              )
            )}
            <span className="text-[11px] text-slate-400">
              {note.updated_at && note.updated_at !== note.created_at ? `Updated ${notifTimeAgo(note.updated_at)}` : notifTimeAgo(note.created_at)}
            </span>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1 opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100">
          <button onClick={(e) => { e.stopPropagation(); onPin(note); }} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-rose-500" title={note.is_pinned ? "Unpin" : "Pin"} data-testid={`note-pin-${note.id}`}>
            {note.is_pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
          </button>
          <button onClick={(e) => { e.stopPropagation(); onConvert(note); }} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-sky-600" title="Convert to Task" data-testid={`note-convert-${note.id}`}>
            <ListTodo className="h-4 w-4" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onEdit(note); }} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Edit" data-testid={`note-edit-${note.id}`}>
            <Pencil className="h-4 w-4" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(note); }} className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600" title="Delete" data-testid={`note-delete-${note.id}`}>
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function NotesPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const highlightId = params.get("highlight");

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("newest");
  const [formOpen, setFormOpen] = useState(false);
  const [editNote, setEditNote] = useState(null);
  const [taskPrefill, setTaskPrefill] = useState(null);
  const [convertNoteId, setConvertNoteId] = useState(null);
  const highlightRef = useRef(null);

  const { data: notes = [], isLoading } = useQuery({
    queryKey: ["notes"],
    queryFn: async () => (await api.get("/notes")).data,
  });

  useEffect(() => {
    if (highlightId && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlightId, notes]);

  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/notes/${id}`)).data,
    onSuccess: () => { toast.success("Note deleted"); qc.invalidateQueries({ queryKey: ["notes"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });
  const pinMut = useMutation({
    mutationFn: async (note) => (await api.patch(`/notes/${note.id}`, { is_pinned: !note.is_pinned })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notes"] }),
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail)),
  });
  const linkTaskMut = useMutation({
    mutationFn: async ({ noteId, taskId }) => (await api.patch(`/notes/${noteId}`, { converted_task_id: taskId })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notes"] }),
  });

  const openNew = () => { setEditNote(null); setFormOpen(true); };
  const openEdit = (n) => { setEditNote(n); setFormOpen(true); };
  const askDelete = (n) => { if (window.confirm("Delete this note? This cannot be undone.")) delMut.mutate(n.id); };
  const startConvert = (nOrForm) => { setFormOpen(false); setEditNote(null); setConvertNoteId(nOrForm.id || null); setTaskPrefill(noteToTaskPrefill(nOrForm)); };

  const { pinned, rest } = useMemo(() => {
    let list = [...notes];
    const q = search.trim().toLowerCase();
    if (q.length >= 2) {
      list = list.filter((n) => (n.title || "").toLowerCase().includes(q) || (n.content || "").toLowerCase().includes(q));
    }
    if (filter === "pinned") list = list.filter((n) => n.is_pinned);
    else if (filter === "reminder") list = list.filter((n) => n.reminder_datetime);
    else if (filter === "linked") list = list.filter((n) => n.linked_record_type);
    else if (filter === "unlinked") list = list.filter((n) => !n.linked_record_type);

    const cmp = (a, b) => {
      if (sort === "newest") return (b.created_at || "").localeCompare(a.created_at || "");
      if (sort === "oldest") return (a.created_at || "").localeCompare(b.created_at || "");
      if (sort === "updated") return (b.updated_at || "").localeCompare(a.updated_at || "");
      if (sort === "reminder") return (a.reminder_datetime || "9999").localeCompare(b.reminder_datetime || "9999");
      return 0;
    };
    const pinnedList = list.filter((n) => n.is_pinned).sort(cmp);
    const restList = list.filter((n) => !n.is_pinned).sort(cmp);
    return { pinned: pinnedList, rest: restList };
  }, [notes, search, filter, sort]);

  const total = pinned.length + rest.length;
  const refFor = (id) => (id === highlightId ? highlightRef : null);

  return (
    <div className="p-4 sm:p-6 lg:p-8 pb-20 max-w-4xl" data-testid="notes-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Notes</h1>
          <p className="text-sm text-slate-500">Your private scratchpad — visible only to you.</p>
        </div>
        <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800" data-testid="new-note-btn">
          <Plus className="h-4 w-4 mr-1.5" /> New Note
        </Button>
      </div>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search notes… (2+ chars)" className="pl-8" data-testid="notes-search" />
        </div>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="sm:w-48" data-testid="notes-sort"><SelectValue /></SelectTrigger>
          <SelectContent>{SORTS.map((s) => <SelectItem key={s.key} value={s.key} data-testid={`notes-sort-${s.key}`}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${filter === f.key ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            data-testid={`notes-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-3">
        {isLoading ? (
          <div className="text-sm text-slate-400" data-testid="notes-loading">Loading…</div>
        ) : total === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-slate-200 py-16 text-center" data-testid="notes-empty">
            <StickyNote className="h-10 w-10 text-slate-300" />
            <p className="mt-3 text-sm text-slate-500">
              {notes.length === 0 ? "No notes yet. Tap + New Note to capture your first thought." : "No notes match your search or filter."}
            </p>
          </div>
        ) : (
          <>
            {pinned.length > 0 && (
              <>
                <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400" data-testid="notes-pinned-header">
                  <Pin className="h-3 w-3" /> Pinned
                </div>
                {pinned.map((n) => (
                  <NoteCard key={n.id} note={n} onEdit={openEdit} onDelete={askDelete} onPin={pinMut.mutate} onConvert={startConvert} highlighted={n.id === highlightId} cardRef={refFor(n.id)} />
                ))}
                {rest.length > 0 && <div className="h-2" />}
              </>
            )}
            {rest.map((n) => (
              <NoteCard key={n.id} note={n} onEdit={openEdit} onDelete={askDelete} onPin={pinMut.mutate} onConvert={startConvert} highlighted={n.id === highlightId} cardRef={refFor(n.id)} />
            ))}
          </>
        )}
      </div>

      <NoteFormDialog
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditNote(null); if (highlightId) { params.delete("highlight"); setParams(params, { replace: true }); } }}
        note={editNote}
        onConvert={startConvert}
      />
      <TaskFormDialog
        open={!!taskPrefill}
        onClose={() => { setTaskPrefill(null); setConvertNoteId(null); }}
        prefill={taskPrefill}
        onCreated={(task) => { if (convertNoteId && task?.id) linkTaskMut.mutate({ noteId: convertNoteId, taskId: task.id }); }}
      />
    </div>
  );
}
